import { useCallback, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type AttentionItem,
  type AttentionKind,
  type CapacityRow,
  type ClientRow,
  type FirmOverview,
  type MoneyByCurrency,
  type PipelineRow,
} from "../lib/api";
import { useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import { Failed, SkeletonRows } from "../components/primitives";
import { day, money, relativeDay } from "../lib/format";

/**
 * Firm — the practice seen whole. Super Admin only.
 *
 * `PRODUCT.md` always described this role as having firm-wide visibility,
 * "across engagements rather than living in one", and nothing implemented it:
 * every other read in the platform takes an engagement id.
 *
 * **On the design, which departs from the rest of the platform deliberately.**
 * `DESIGN.md` refuses a dashboard of tiles for internal surfaces, because a
 * tile hides the one property the system is built on — that facts are written
 * and never edited away. That refusal is right for Command, Delivery and
 * Files, which are *records of a job*. This surface is not a record. It is an
 * instrument panel for one person, answering "how is the practice", and the
 * user asked for it as a dashboard knowingly.
 *
 * The refusal is honoured where it still bites: **every figure here is a
 * count, never a verdict**, and the largest single element on the screen is
 * the attention register — real rows, ordered by how late they are, each
 * opening into the engagement's own record. Nothing can be acted on from
 * here. You read the panel, then you go and read the record.
 *
 * Ink ground, set on the shell so `.workspace-grid` comes with it. Firm is
 * the only internal surface that is not warm paper, which is the point:
 * walking in should feel like a different room.
 */
export function Firm() {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <PinGate onUnlock={() => setUnlocked(true)} />;
  return <FirmBody />;
}

/* --------------------------------------------------------------- the pin ---

   Theatre, written down as theatre so nobody later mistakes it for a control.

   The PIN is in the browser bundle. Anyone with developer tools reads it in
   seconds, and six digits is a million guesses, which is nothing. It protects
   precisely nothing and is not meant to.

   What protects this screen is two floors down and does not know the PIN
   exists: app.firm_overview() and app.firm_attention() are SECURITY DEFINER
   functions that each re-check app.is_super_admin(). Someone who types the
   right digits, or who skips this component and calls the endpoint directly,
   gets nothing unless they are the Super Admin.

   The one thing never to do is move it server-side, which would dress a
   flourish up as a second factor.
   --------------------------------------------------------------------------- */

const PIN = "777999";

function PinGate({ onUnlock }: { onUnlock: () => void }) {
  const [digits, setDigits] = useState("");
  const [wrong, setWrong] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = useCallback(
    (value: string) => {
      if (value === PIN) return onUnlock();
      setWrong(true);
      setDigits("");
      window.setTimeout(() => setWrong(false), 600);
    },
    [onUnlock],
  );

  const change = (raw: string) => {
    const next = raw.replace(/\D/g, "").slice(0, 6);
    setDigits(next);
    if (next.length === 6) submit(next);
  };

  return (
    <div
      data-world="ink"
      className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6"
    >
      <p className="label">Arikekpar &amp; Company</p>
      <h1 className="mt-4 font-editorial text-3xl text-ink">Firm</h1>
      <p className="mt-3 text-sm text-secondary">
        Enter the six-digit Firm access code.
      </p>

      {/* One real input behind six cells. Keeps mobile keyboards, paste and
          autofill working, which six separate boxes famously break. */}
      <div
        className={`relative mt-10 ${wrong ? "motion-safe:animate-[shake_0.4s]" : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          autoFocus
          inputMode="numeric"
          autoComplete="off"
          aria-label="Six digit access code"
          value={digits}
          onChange={(e) => change(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-default opacity-0"
        />
        <div className="pointer-events-none flex gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className={`flex h-16 w-12 items-center justify-center bg-panel text-2xl text-ink shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] transition-colors duration-150 ${
                wrong
                  ? "ring-1 ring-danger"
                  : i === digits.length
                    ? "ring-1 ring-sand"
                    : "ring-1 ring-white/10"
              }`}
            >
              {digits[i] ? "•" : ""}
            </span>
          ))}
        </div>
      </div>

      <p
        className={`mt-6 text-sm transition-opacity duration-150 ${
          wrong ? "text-danger opacity-100" : "opacity-0"
        }`}
        role={wrong ? "alert" : undefined}
      >
        That code is incorrect. Try again.
      </p>

      <Link
        to="/"
        className="mt-10 text-sm text-secondary underline-offset-4 hover:underline"
      >
        Back
      </Link>
    </div>
  );
}

/* ------------------------------------------------------------ the panels -- */

function FirmBody() {
  const overview = useResource<FirmOverview>(
    () => api.get<FirmOverview>("/api/firm/overview"),
    [],
  );
  const attention = useResource<AttentionItem[]>(
    () => api.get<AttentionItem[]>("/api/firm/attention"),
    [],
  );

  const o = overview.resource.state === "ready" ? overview.resource.data : null;

  return (
    <AppShell world="ink">
      <ScreenHead title="Firm" />

      {overview.resource.state === "loading" && <SkeletonRows rows={4} />}
      {overview.resource.state === "failed" && (
        <Failed
          message={overview.resource.message}
          correlationId={overview.resource.correlationId}
        />
      )}

      {o && (
        <>
          <p className="-mt-2 text-xs text-secondary">
            as of {new Date(o.asOf).toLocaleTimeString()}
          </p>

          {/* Six panels, floating. Restraint is held by the rules rather than
              by willpower: one figure per panel, one supporting line, no
              colour except where something is actually late. */}
          <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Panel
              label="Clients"
              figure={o.clients.total}
              note={`${o.clients.active} with live work`}
            />
            <Panel
              label="Engagements"
              figure={o.engagementsTotal}
              note={
                Object.entries(o.engagements)
                  .map(([s, n]) => `${n} ${s}`)
                  .join(" · ") || "none yet"
              }
            />
            <Panel
              label="People"
              figure={Object.values(o.people.byRole).reduce((a, b) => a + b, 0)}
              note={`${o.people.consultantsEngaged} consultants engaged${
                o.people.invitationsPending
                  ? ` · ${o.people.invitationsPending} invited`
                  : ""
              }`}
            />
            <Panel
              label="Client portals"
              figure={o.portals.activated}
              note={
                o.portals.readyNotActivated
                  ? `${o.portals.readyNotActivated} readied, never opened`
                  : "none waiting"
              }
              warn={o.portals.readyNotActivated > 0}
            />
            <Panel
              label="Work in hand"
              figure={o.work.tasksOpen}
              note={`${o.work.deliverablesAwaitingApproval} awaiting a decision · ${o.work.clientActionsOpen} with the client`}
            />
            <Panel
              label="Overdue"
              figure={o.work.tasksOverdue}
              note={
                o.work.deliverablesInRevision
                  ? `${o.work.deliverablesInRevision} in revision`
                  : "nothing in revision"
              }
              warn={o.work.tasksOverdue > 0}
            />
          </div>
        </>
      )}

      <Money />

      {/* The register. Deliberately the largest thing on the screen — the
          panels above are an index to it, not a replacement for it. */}
      <section className="mt-6 bg-panel p-6 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] ring-1 ring-white/10 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="label">Needs attention, across every engagement</h2>
          {o && (
            <span className="text-xs text-secondary">
              {o.documents.total} documents · {formatBytes(o.documents.storedBytes)}
            </span>
          )}
        </div>

        <div className="mt-5">
          {attention.resource.state === "loading" && <SkeletonRows rows={5} />}
          {attention.resource.state === "failed" && (
            <Failed
              message={attention.resource.message}
              correlationId={attention.resource.correlationId}
            />
          )}
          {attention.resource.state === "ready" &&
            attention.resource.data.length === 0 && (
              <p className="py-6 text-base text-secondary">
                Nothing is late and nothing is waiting. No overdue task, no
                deliverable awaiting a decision, no client action outstanding,
                and no assignment expiring in the next fortnight.
              </p>
            )}
          {attention.resource.state === "ready" &&
            attention.resource.data.map((item, i) => (
              <AttentionRow key={`${item.kind}-${item.engagementId}-${i}`} item={item} />
            ))}
        </div>
      </section>

      <Pipeline />
      <Clients />
      <Capacity />
    </AppShell>
  );
}

/* ----------------------------------------------------------------- money --

   The one thing this screen never had, and the first question anyone
   running a practice opens it with.

   **One block per currency, and no total anywhere.** 033 wrote the rule and
   043 keeps it in SQL: a firm billing in more than one currency must not add
   them up. Naira plus dollars is not a number, and the single person reading
   this screen is the one most likely to believe a figure simply because it
   is large and bold. Two currencies means two rows, stacked, each complete
   in itself.

   Billed / collected / outstanding, with overdue called out only when there
   is some — the same restraint as the panels above: one red numeral among
   several black ones, never a block that lights up wholesale. */

function Money() {
  const { resource } = useResource<MoneyByCurrency[]>(
    () => api.get<MoneyByCurrency[]>("/api/firm/money"),
    [],
  );

  if (resource.state === "loading") return <SkeletonRows rows={2} />;
  if (resource.state === "failed") {
    return (
      <Failed message={resource.message} correlationId={resource.correlationId} />
    );
  }

  const rows = resource.state === "ready" ? resource.data : [];

  return (
    <section className="mt-6 bg-panel p-6 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] ring-1 ring-white/10 sm:p-8">
      <h2 className="label">Money, by currency</h2>

      {rows.length === 0 ? (
        <p className="mt-5 text-base text-secondary">
          Nothing has been invoiced yet. A draft is not counted here — only
          what has actually been sent to a client.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-7">
          {rows.map((m) => (
            <div key={m.currency}>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="font-mono text-xs uppercase tracking-chip text-secondary">
                  {m.currency}
                </span>
                <span className="text-sm text-secondary">
                  {m.invoices} invoice{m.invoices === 1 ? "" : "s"} sent
                </span>
              </div>

              <div className="mt-3 grid gap-5 sm:grid-cols-3">
                <Amount label="Billed" value={m.billed} currency={m.currency} />
                <Amount label="Collected" value={m.collected} currency={m.currency} />
                <Amount
                  label="Outstanding"
                  value={m.outstanding}
                  currency={m.currency}
                  warn={m.overdue > 0}
                  note={
                    m.overdue > 0
                      ? `${money(m.overdue, m.currency)} of it overdue, on ${m.overdueInvoices} invoice${m.overdueInvoices === 1 ? "" : "s"}`
                      : "none of it overdue"
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Amount({
  label,
  value,
  currency,
  note,
  warn = false,
}: {
  label: string;
  value: number;
  currency: string;
  note?: string;
  warn?: boolean;
}) {
  return (
    <div>
      <p className="label">{label}</p>
      <p
        className={`mt-2 font-editorial text-3xl tabular-nums leading-none ${
          warn ? "text-danger" : "text-ink"
        }`}
      >
        {money(value, currency)}
      </p>
      {note && <p className="mt-2 text-sm text-secondary">{note}</p>}
    </div>
  );
}

/* -------------------------------------------------------------- pipeline --

   Live work, soonest to end first. The question underneath it is not "what
   are we doing" — the engagement list answers that — but "what runs out
   next", which is the one nobody asks until it already has.

   Undated engagements sort last and are kept. Not knowing when something
   ends is itself worth seeing, and dropping those rows would quietly shorten
   the list somebody is planning from. */

function Pipeline() {
  const { resource } = useResource<PipelineRow[]>(
    () => api.get<PipelineRow[]>("/api/firm/pipeline"),
    [],
  );
  const rows = resource.state === "ready" ? resource.data : [];

  return (
    <Block title="Ending soonest" state={resource}>
      {rows.length === 0 ? (
        <p className="py-6 text-base text-secondary">No live engagements.</p>
      ) : (
        rows.map((r) => (
          <Link
            key={r.engagementId}
            to={`/engagements/${r.engagementId}`}
            className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 py-3 transition-colors last:border-b-0 hover:bg-white/[0.03]"
          >
            <div className="min-w-0">
              <p className="text-base text-ink">{r.engagement}</p>
              <p className="mt-1 text-sm text-secondary">
                {r.organisation}{" "}
                <span className="font-mono text-xs">· {r.reference}</span>
              </p>
            </div>
            <div className="flex items-baseline gap-4">
              {r.billed > 0 && r.currency && (
                <span className="text-sm tabular-nums text-secondary">
                  {money(r.billed, r.currency)} billed
                </span>
              )}
              <span className="label">{r.status}</span>
              {r.targetCompletion ? (
                <span
                  className={`text-sm tabular-nums ${
                    r.daysLeft !== null && r.daysLeft < 0
                      ? "text-danger"
                      : "text-secondary"
                  }`}
                >
                  {r.daysLeft !== null && r.daysLeft < 0
                    ? `${Math.abs(r.daysLeft)} days over`
                    : relativeDay(r.targetCompletion)}
                </span>
              ) : (
                <span className="text-sm text-secondary">no end date</span>
              )}
            </div>
          </Link>
        ))
      )}
    </Block>
  );
}

/* --------------------------------------------------------------- clients --

   The firm's relationships rather than a headcount. What they are worth,
   what they owe, and when anything last happened on their work.

   A client billed in two currencies appears twice, for the reason in the
   money block. A client never billed still appears — they are a client, and
   dropping them would turn this into a list of debtors. */

function Clients() {
  const { resource } = useResource<ClientRow[]>(
    () => api.get<ClientRow[]>("/api/firm/clients"),
    [],
  );
  const rows = resource.state === "ready" ? resource.data : [];

  return (
    <Block title="Clients" state={resource}>
      {rows.length === 0 ? (
        <p className="py-6 text-base text-secondary">No client organisations yet.</p>
      ) : (
        rows.map((c) => (
          <div
            key={`${c.organisationId}-${c.currency ?? "none"}`}
            className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 py-3 last:border-b-0"
          >
            <div className="min-w-0">
              <p className="text-base text-ink">{c.organisation}</p>
              <p className="mt-1 text-sm text-secondary">
                {c.engagements} engagement{c.engagements === 1 ? "" : "s"}
                {c.live > 0 ? ` · ${c.live} live` : " · none live"}
                {c.lastActivity
                  ? ` · last touched ${relativeDay(c.lastActivity)}`
                  : " · nothing recorded yet"}
              </p>
            </div>
            <div className="flex items-baseline gap-4 tabular-nums">
              {c.currency ? (
                <>
                  <span className="text-sm text-secondary">
                    {money(c.billed, c.currency)} billed
                  </span>
                  {c.outstanding > 0 && (
                    <span className="text-sm text-danger">
                      {money(c.outstanding, c.currency)} owed
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm text-secondary">never billed</span>
              )}
            </div>
          </div>
        ))
      )}
    </Block>
  );
}

/* -------------------------------------------------------------- capacity --

   Who the firm can put on something.

   "Freeing" is the state worth having: by the time somebody is free they
   have been idle for a while, and by the time an engagement ends it is too
   late to have sold the next one. Thirty days is the window, set in SQL so
   this screen and the database cannot disagree about it. */

const CAPACITY_LABEL: Record<CapacityRow["state"], string> = {
  engaged: "Engaged",
  freeing: "Freeing up",
  free: "Free",
};

function Capacity() {
  const { resource } = useResource<CapacityRow[]>(
    () => api.get<CapacityRow[]>("/api/firm/capacity"),
    [],
  );
  const rows = resource.state === "ready" ? resource.data : [];
  const free = rows.filter((r) => r.state === "free").length;
  const freeing = rows.filter((r) => r.state === "freeing").length;

  return (
    <Block
      title="Capacity"
      state={resource}
      aside={
        rows.length
          ? `${free} free · ${freeing} freeing up within 30 days`
          : undefined
      }
    >
      {rows.length === 0 ? (
        <p className="py-6 text-base text-secondary">
          No consultants on the books yet.
        </p>
      ) : (
        rows.map((r) => (
          <div
            key={r.userId}
            className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 py-3 last:border-b-0"
          >
            <p className="min-w-0 text-base text-ink">{r.fullName}</p>
            <div className="flex items-baseline gap-4">
              <span className="text-sm text-secondary">
                {r.engagements === 0
                  ? "nothing on"
                  : `${r.engagements} engagement${r.engagements === 1 ? "" : "s"}`}
              </span>
              {r.earliestEnd && (
                <span className="text-sm tabular-nums text-secondary">
                  first ends {day(r.earliestEnd)}
                </span>
              )}
              <span
                className={`label ${r.state === "free" ? "text-sand" : ""}`}
              >
                {CAPACITY_LABEL[r.state]}
              </span>
            </div>
          </div>
        ))
      )}
    </Block>
  );
}

/** The shared frame for the three lists below the register. */
function Block({
  title,
  aside,
  state,
  children,
}: {
  title: string;
  aside?: string;
  state: { state: string; message?: string; correlationId?: string };
  children: ReactNode;
}) {
  return (
    <section className="mt-6 bg-panel p-6 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] ring-1 ring-white/10 sm:p-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="label">{title}</h2>
        {aside && <span className="text-xs text-secondary">{aside}</span>}
      </div>
      <div className="mt-5">
        {state.state === "loading" && <SkeletonRows rows={4} />}
        {state.state === "failed" && (
          <Failed
            message={state.message ?? "That could not be read."}
            correlationId={state.correlationId}
          />
        )}
        {state.state === "ready" && children}
      </div>
    </section>
  );
}

/**
 * One floating panel. A figure, a label, and one line of context.
 *
 * `warn` tints the figure and nothing else — the panel never changes colour
 * wholesale. A wall that lights up red stops meaning anything by the third
 * time you see it; one red numeral among five black ones does not.
 */
function Panel({
  label,
  figure,
  note,
  warn = false,
}: {
  label: string;
  figure: ReactNode;
  note: string;
  warn?: boolean;
}) {
  return (
    <div className="bg-panel p-6 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] ring-1 ring-white/10 transition-shadow duration-200 hover:ring-white/20">
      <p className="label">{label}</p>
      <p
        className={`mt-4 font-editorial text-5xl tabular-nums leading-none ${
          warn ? "text-danger" : "text-ink"
        }`}
      >
        {figure}
      </p>
      <p className="mt-3 text-sm text-secondary">{note}</p>
    </div>
  );
}

const KIND_LABEL: Record<AttentionKind, string> = {
  task_overdue: "Overdue",
  awaiting_approval: "Awaiting decision",
  revision_requested: "In revision",
  client_action_open: "With the client",
  assignment_expiring: "Access ending",
  invoice_overdue: "Unpaid",
};

function AttentionRow({ item }: { item: AttentionItem }) {
  return (
    <Link
      to={`/engagements/${item.engagementId}`}
      className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 py-3 transition-colors last:border-b-0 hover:bg-white/[0.03]"
    >
      <div className="min-w-0">
        <p className="text-base text-ink">{item.subject}</p>
        <p className="mt-1 text-sm text-secondary">
          {item.engagement}{" "}
          <span className="font-mono text-xs">· {item.reference}</span>
        </p>
      </div>

      <div className="flex items-baseline gap-4">
        <span className="label">{KIND_LABEL[item.kind]}</span>
        {item.dueOn && (
          <span
            className={`text-sm tabular-nums ${
              item.daysLate > 0 ? "text-danger" : "text-secondary"
            }`}
          >
            {item.daysLate > 0
              ? `${item.daysLate} day${item.daysLate === 1 ? "" : "s"} late`
              : day(item.dueOn)}
          </span>
        )}
      </div>
    </Link>
  );
}

/** Stored bytes, for a line that should never need a calculator to read. */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} kB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
